import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

export const PANE_WIDTH = { start: 420, least: 320, collapseBelow: 240, most: 760, boardLeast: 720 };

export function useHostWidth(): [(element: HTMLDivElement | null) => void, number] {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!host) return undefined;
    setWidth(host.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(host);
    return () => observer.disconnect();
  }, [host]);
  return [setHost, width];
}

const STORED = 'lens-pane-width';

export function storedPaneWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(STORED));
    return Number.isFinite(stored) && stored >= PANE_WIDTH.least ? stored : PANE_WIDTH.start;
  } catch {
    return PANE_WIDTH.start;
  }
}

function rememberPaneWidth(width: number) {
  try {
    window.localStorage.setItem(STORED, String(Math.round(width)));
  } catch {
    return;
  }
}

type PaneEdgeProps = { width: number; most: number; onWidth: (width: number) => void; onCollapse: () => void };

export default function PaneEdge({ width, most, onWidth, onCollapse }: PaneEdgeProps) {
  const drag = useRef<{ right: number; start: number } | undefined>(undefined);
  const [closing, setClosing] = useState(false);
  const wanted = (clientX: number) => drag.current!.right - clientX;
  const clamp = (value: number) => Math.min(Math.max(value, PANE_WIDTH.least), most);
  return (
    <Box
      aria-label="Resize the panel"
      aria-orientation="vertical"
      aria-valuemax={most}
      aria-valuemin={PANE_WIDTH.least}
      aria-valuenow={Math.round(width)}
      onDoubleClick={() => {
        onWidth(PANE_WIDTH.start);
        rememberPaneWidth(PANE_WIDTH.start);
      }}
      onKeyDown={(event) => {
        const step = event.key === 'ArrowLeft' ? 24 : event.key === 'ArrowRight' ? -24 : 0;
        if (!step) return;
        event.preventDefault();
        onWidth(clamp(width + step));
        rememberPaneWidth(clamp(width + step));
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const pane = event.currentTarget.parentElement!.getBoundingClientRect();
        drag.current = { right: pane.right, start: width };
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        const next = wanted(event.clientX);
        setClosing(next < PANE_WIDTH.collapseBelow);
        onWidth(clamp(next));
      }}
      onPointerUp={(event) => {
        if (!drag.current) return;
        const next = wanted(event.clientX);
        const { start } = drag.current;
        drag.current = undefined;
        setClosing(false);
        if (next < PANE_WIDTH.collapseBelow) {
          onWidth(start);
          onCollapse();
        } else rememberPaneWidth(clamp(next));
      }}
      role="separator"
      sx={{
        bottom: 0,
        cursor: 'col-resize',
        left: -4,
        position: 'absolute',
        top: 0,
        touchAction: 'none',
        width: 8,
        zIndex: 2,
        '&::after': { bgcolor: closing ? 'error.main' : 'primary.main', bottom: 0, content: '""', left: 3, opacity: 0, position: 'absolute', top: 0, transition: 'opacity 120ms', width: 2 },
        '&:hover::after, &:focus-visible::after': { opacity: 0.6 },
        ...(closing ? { '&::after': { bgcolor: 'error.main', bottom: 0, content: '""', left: 3, opacity: 0.8, position: 'absolute', top: 0, width: 2 } } : {}),
      }}
      tabIndex={0}
    />
  );
}
